import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, List } from 'components/base';
import produce from 'utils/produce';
import styled from 'styled-components';
import { Button } from '../../components/base';
import Checkbox, { CheckboxGrid, CheckboxGridTitle } from '../../components/base/checkbox';
import { getPageOptionLabel } from 'editor-utils/template';

const ErrorsMessages = styled.div``;

const OptionCheckboxStyles = styled(Checkbox)`
  margin: 0.5em !important;
`;

const Option = styled.div`
  padding: 0.2em;
  padding-top: 0;
`;

const OptionsModal = styled.div`
  padding: 1rem !important;
  background: white;
  border: 0.0625rem solid rgba(39, 48, 112, 0.1);
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10000;
  min-width: 400px;
  max-height: 90vh;
  overflow-y: scroll;
  box-shadow: 0 0.125rem 0.25rem 0 rgba(28, 61, 96, 0.06);
`;

const IssueCritical = styled(List.Item)`
  &::before {
    background-color: red !important;
  }

  color: red;
`;

const IssueWarning = styled(List.Item)`
  &::before {
    background-color: orange !important;
  }

  color: orange;
`;

const HRStyles = styled.hr`
  border-top: 1px solid rgba(21, 21, 28, 0.1);
  margin-bottom: 1rem !important;
  margin-top: 0.5rem;
`;

const HRWideStyles = styled.hr`
  border-top: 2px solid rgba(21, 21, 28, 0.2);
  margin-bottom: 1rem !important;
  margin-top: 1rem;
`;

const H4Styles = styled(Typography.H4)`
  text-transform: none !important;
`;

const ListStyles = styled(List)`
  margin-bottom: 1.2rem !important;
`;

const OptionedContinueButton = props => {
  const ref = useRef();

  const [issues, setIssues] = useState([]);

  const [options, setOptions] = useState([]);

  const [confirmations, setConfirmations] = useState([]);

  const handleOnClickOutside = e => {
    if (ref.current) {
      if (ref.current !== e.target && !ref.current.contains(e.target)) {
        setIssues([]);
        setOptions([]);
        setConfirmations([]);
      }
    } else {
      setIssues([]);
      setOptions([]);
      setConfirmations([]);
    }
  };

  useEffect(() => {
    if (issues.length > 0 || options.length > 0) {
      setTimeout(() => document.addEventListener('click', handleOnClickOutside));
    } else {
      document.removeEventListener('click', handleOnClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleOnClickOutside);
    };
  }, [issues, options]);

  const handleOnOptionChange = (index, checked) => {
    setOptions(val =>
      produce(val, draft => {
        draft[index].checked = checked;
      }),
    );
  };

  const handleOnConfirmationChange = (index, checked) => {
    setConfirmations(val =>
      produce(val, draft => {
        draft[index].checked = checked;
      }),
    );
  };

  const handleOnFixTheIssuesClick = e => {
    e.preventDefault();

    setIssues([]);
  };

  const handleOnContinueClick = async (e, firstCall = true) => {
    e.preventDefault();

    // CheckErrors
    if (props.checkErrors) {
      const checkErrors = firstCall === true;
      const { errors } = await props.onClick(checkErrors);

      if (errors) {
        setIssues(errors);
        setConfirmations(errors.filter(issue => issue.type === 'confirmation'));
      } else {
        setIssues([]);
        setConfirmations([]);
      }
      return;
    }

    // Has Options
    if (props.options.length > 0) {
      if (firstCall) {
        setOptions(
          props.options.map(option => ({
            label: option,
            checked: true,
          })),
        );
      } else {
        const checkedOptions = options.filter(option => option.checked).map(option => option.label);
        props.onClick(checkedOptions);
        setOptions([]);
      }
      return;
    }

    // Dont check errors and has no options
    props.onClick([]);
  };

  const criticalIssues = [];
  const warningIssues = [];

  // filter issues
  issues.forEach(issue => {
    if (issue.type === 'critical') {
      criticalIssues.push(issue);
    } else if (issue.type === 'warning') {
      warningIssues.push(issue);
    }
  });

  const enableContinue = criticalIssues.length === 0;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <Button
        variant={props.variant}
        disabled={props.disabled}
        onClick={e => handleOnContinueClick(e, true)}
      >
        {props.children}
      </Button>
      {(options.length > 0 || issues.length > 0) && (
        <OptionsModal>
          {options.length > 0 && (
            <CheckboxGrid>
              <CheckboxGridTitle>
                Please select the page types you want to include:
              </CheckboxGridTitle>
              {options.map((option, index) => (
                <OptionCheckboxStyles
                  key={option.label}
                  checked={option.checked}
                  onChange={checked => handleOnOptionChange(index, checked)}
                >
                  <Option>{getPageOptionLabel(option.label)}</Option>
                </OptionCheckboxStyles>
              ))}
            </CheckboxGrid>
          )}
          {issues.length > 0 && (
            <>
              <Typography.H3>One final check...</Typography.H3>
              <HRWideStyles />

              {criticalIssues.length > 0 && (
                <ErrorsMessages>
                  <H4Styles>Errors: (Fix required)</H4Styles>
                  <HRStyles />
                  <ListStyles styled>
                    {criticalIssues.map(issue => (
                      <IssueCritical key={issue.text}>{issue.text}</IssueCritical>
                    ))}
                  </ListStyles>
                </ErrorsMessages>
              )}
              {warningIssues.length > 0 && (
                <ErrorsMessages>
                  <H4Styles>Warnings: (Fix optional)</H4Styles>
                  <HRStyles />
                  <ListStyles styled>
                    {warningIssues.map(issue => (
                      <IssueWarning key={issue.text}>{issue.text}</IssueWarning>
                    ))}
                  </ListStyles>
                </ErrorsMessages>
              )}
              {enableContinue && confirmations.length > 0 && (
                <CheckboxGrid>
                  <CheckboxGridTitle>Reconfirm Review</CheckboxGridTitle>
                  {confirmations.map((issue, index) => (
                    <OptionCheckboxStyles
                      key={issue.text}
                      checked={issue.checked}
                      onChange={checked => handleOnConfirmationChange(index, checked)}
                    >
                      <Option>{issue.text}</Option>
                    </OptionCheckboxStyles>
                  ))}
                </CheckboxGrid>
              )}
            </>
          )}
          {enableContinue ? (
            <>
              <Button
                variant={props.variant}
                fullWidth
                mt={2}
                onClick={e => handleOnContinueClick(e, false)}
                disabled={!confirmations.every(({ checked }) => checked)}
              >
                {props.children}
              </Button>
              {issues.length > 0 && (
                <Button fullWidth mt={1} onClick={handleOnFixTheIssuesClick}>
                  Go back
                </Button>
              )}
            </>
          ) : (
            <Button variant={props.variant} fullWidth mt={2} onClick={handleOnFixTheIssuesClick}>
              Fix the issues
            </Button>
          )}
        </OptionsModal>
      )}
    </div>
  );
};

OptionedContinueButton.propTypes = {
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array,
  checkErrors: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

OptionedContinueButton.defaultProps = {
  variant: 'primary',
  disabled: false,
  options: [],
  checkErrors: true,
};

export default OptionedContinueButton;
